/**
 * Adds a new slide (title + bullet body) to the active presentation.
 *
 * Unlike the shared slideOps.insertTextOnSlide (which returns false and
 * swallows errors), this THROWS a descriptive error on failure so the Create-
 * slides wizard can surface exactly why a slide wasn't added — silent failure
 * is what made the progress list claim success when nothing was inserted.
 */
/**
 * Inserts slide(s) from a directly-insertable base64 `.pptx` (the backend's
 * designed output) at the end of the active deck, preserving source formatting.
 * This is the preferred render path — the backend returns a base64 `.pptx` and
 * the FE inserts it verbatim. Throws a descriptive error on failure.
 */
export async function insertSlideFromBase64(base64Pptx: string): Promise<void> {
  const g = globalThis as { PowerPoint?: typeof PowerPoint };
  if (!g.PowerPoint) {
    throw new Error("PowerPoint API unavailable — not running inside PowerPoint.");
  }

  await g.PowerPoint.run(async (ctx) => {
    const presentation = ctx.presentation;
    const presAny = presentation as unknown as {
      insertSlidesFromBase64?: (b: string, o?: unknown) => void;
    };
    if (typeof presAny.insertSlidesFromBase64 !== "function") {
      throw new TypeError(
        "This PowerPoint version can't insert slides from base64 (needs PowerPointApi 1.2+).",
      );
    }

    const slides = presentation.slides;
    slides.load("items/id");
    await ctx.sync();

    const lastId = slides.items.length ? slides.items[slides.items.length - 1]!.id : undefined;
    presAny.insertSlidesFromBase64(
      base64Pptx,
      lastId
        ? { formatting: "KeepSourceFormatting", targetSlideId: lastId }
        : { formatting: "KeepSourceFormatting" },
    );
    await ctx.sync();
  });
}

/**
 * Adds a new slide on a placeholder-free ("Blank") layout and syncs. The default
 * `slides.add()` uses the master's first layout — usually "Title Slide", which
 * carries "Click to add title/subtitle" prompt placeholders that bleed through
 * any content we draw ourselves. Resolving a Blank layout removes those prompts.
 * Falls back to the host default if the master/layouts can't be read.
 */
export async function addBlankSlide(
  ctx: PowerPoint.RequestContext,
  slides: PowerPoint.SlideCollection,
): Promise<void> {
  const slidesAny = slides as unknown as { add?: (opts?: unknown) => void };
  if (typeof slidesAny.add !== "function") {
    throw new TypeError(
      "This PowerPoint version can't add slides via the API (needs PowerPointApi 1.5+; use PowerPoint on the web or update desktop).",
    );
  }

  let addOpts: { slideMasterId: string; layoutId: string } | undefined;
  try {
    const master = ctx.presentation.slideMasters.getItemAt(0);
    master.load("id");
    const layouts = master.layouts;
    layouts.load("items/id,items/name");
    await ctx.sync();
    // "Blank" is the standard placeholder-free layout; fall back to the last
    // layout (conventionally Blank) when the name doesn't match (locale, etc.).
    const blank =
      layouts.items.find((l) => /blank/i.test(l.name)) ??
      layouts.items[layouts.items.length - 1];
    if (blank) addOpts = { slideMasterId: master.id, layoutId: blank.id };
  } catch (e) {
    // Master/layout not accessible on this host — fall back to the default add.
    console.warn("[create-slide] blank layout lookup failed; using default layout:", e);
  }

  try {
    slidesAny.add(addOpts);
    await ctx.sync();
  } catch (e) {
    // A rejected layoutId is rare; retry with the host default so we still add.
    console.warn("[create-slide] blank layout add rejected; retrying with default:", e);
    slidesAny.add();
    await ctx.sync();
  }
}

export async function addContentSlide(title: string, bullets: string[]): Promise<void> {
  const g = globalThis as { PowerPoint?: typeof PowerPoint };
  if (!g.PowerPoint) {
    throw new Error("PowerPoint API unavailable — not running inside PowerPoint.");
  }

  await g.PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    await addBlankSlide(ctx, slides);

    const count = slides.getCount();
    await ctx.sync();

    const slide = slides.getItemAt(count.value - 1);
    const titleBox = slide.shapes.addTextBox(title || "Untitled", {
      left: 40,
      top: 28,
      width: 880,
      height: 70,
    });
    try {
      titleBox.textFrame.textRange.font.size = 28;
      titleBox.textFrame.textRange.font.bold = true;
    } catch {
      /* font API may be unavailable on this host */
    }

    const body = bullets.filter(Boolean).map((b) => `• ${b}`).join("\n");
    if (body) {
      const bodyBox = slide.shapes.addTextBox(body, {
        left: 40,
        top: 120,
        width: 880,
        height: 360,
      });
      try {
        bodyBox.textFrame.textRange.font.size = 18;
      } catch {
        /* skip */
      }
    }

    await ctx.sync();
  });
}
